import React from "react";
import styled, { css } from "styled-components";
import _ from "lodash";

import Graph from "src/library/components/Graph";
import {
  TotalTicketsByStatusResult,
  useWidgetDataApi
} from "src/core/dashboard/api/widget-data.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { GraphPayload } from "src/library/components/Graph/GraphToolTip";
import { useWidgetContainerSize } from "src/core/dashboard/hooks";
import { SupportTicketStatus } from "src/modules/support/api/support.api";
import { WidgetDisplayTypes } from "src/core/dashboard/api/dashboard.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import {
  getDisplayRange,
  getGraphMetrics,
  DisplayRangeType,
  getSelectedWidgetView,
  WidgetProps
} from "../utils";
import DataLoadError from "../DataLoadError";
import { groupTotalTicketsByStatus } from "./utils";
import { WidgetInnerHeader } from "../WidgetInnerHeader";
import { NumberWidget } from "../NumberWidget";
import { useWidgetFontSize } from "../hooks";

// Type for the flat object used to display data in graph
export interface TotalTicketsByStatusGraphValue extends Omit<GraphPayload, "date"> {
  /** xAxisBy date */
  date?: Date;
  [ SupportTicketStatus.AdminResolved ]?: number;
  [ SupportTicketStatus.CustomerResolved ]?: number;
  [ SupportTicketStatus.New ]?: number;
  [ SupportTicketStatus.PendingAdminResponse ]?: number;
  [ SupportTicketStatus.PendingCustomerResponse ]?: number;
  /** xAxisBy status */
  status?: SupportTicketStatus;
  count?: number;
}

const SUPPORT_TOTAL_TICKETS_BY_STATUS_CACHE_KEY = "widget_support_total_tickets_by_status";

/**
 * Returns total tickets by status widget content
 */
export const TotalTicketsByStatusWidget: React.FunctionComponent<WidgetProps> = ({
  options, filters, widgetTypeId, widgetId, ignoreCache = false
}) => {
  const [ displayRange, setDisplayRange ] = React.useState<DisplayRangeType>("day");
  const [ totalTicketsByStatusResponse, totalTicketsByStatusRequest ] = useWidgetDataApi("GET_TOTAL_TICKETS_BY_STATUS");
  const [ xAxisBy, setXAisBy ] = React.useState<"date" | "status">("date");
  const wrapperRef = React.createRef<HTMLDivElement>();
  const { width } = useWidgetContainerSize(wrapperRef);
  const fontSize = useWidgetFontSize(width, options.view);

  const selectedView = React.useMemo(() => {
    return getSelectedWidgetView(options.view);
  }, [ options ]);

  // On load, get the data we need
  React.useEffect(() => {
    totalTicketsByStatusRequest({
      widgetTypeId,
      filters,
      options: null
    }, {
      cache: {
        maxAge: cacheMinutesToMilliseconds(60),
        key: () => SUPPORT_TOTAL_TICKETS_BY_STATUS_CACHE_KEY,
        ignoreCache
      }
    });
  }, [
    filters,
    ignoreCache,
    totalTicketsByStatusRequest,
    widgetTypeId
  ]);

  // Takes the raw response from API and transforms the result to the shape required for graph data.
  // During this process, we also sets the displayRange & xAxisBy state
  const transformToGraphData = React.useCallback((totalTicketsByStatusResult: TotalTicketsByStatusResult): TotalTicketsByStatusGraphValue[] => {
    // Find range to determine whether to display in days, weeks or months
    const calculatedDisplayRange = getDisplayRange(totalTicketsByStatusResult);

    // Update state required by Graph component
    setDisplayRange(calculatedDisplayRange);
    setXAisBy(calculatedDisplayRange === "day" ? "status" : "date");

    // Group, accumulate totals and return
    return groupTotalTicketsByStatus(totalTicketsByStatusResult, xAxisBy, calculatedDisplayRange);
  }, [ xAxisBy ]);

  // The data used to display metrics
  const graphDisplayData = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      if (totalTicketsByStatusResponse.data?.result) {
        return transformToGraphData(totalTicketsByStatusResponse.data?.result);
      }
    }

    return null;
  }, [
    totalTicketsByStatusResponse.data?.result,
    transformToGraphData,
    selectedView
  ]);

  const graphMetrics = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      return graphDisplayData ? getGraphMetrics(graphDisplayData, [ xAxisBy ]) : [];
    }

    return null;
  }, [
    graphDisplayData,
    selectedView,
    xAxisBy
  ]);

  // The data used to display number metrics
  const numberDisplayData = React.useMemo(() => {
    if (selectedView === WidgetDisplayTypes.Number) {
      const totalTicketsByStatusResult = totalTicketsByStatusResponse.data?.result;

      if (totalTicketsByStatusResult) {
        const totalTicketsByStatus = groupTotalTicketsByStatus(totalTicketsByStatusResult, "status") as Array<{ status: string, count: number }>;

        return {
          value: totalTicketsByStatusResult.reduce((total, ticket) => total + ticket.count, 0),
          data: totalTicketsByStatus.map(statusData => {
            return {
              group: _.startCase(statusData.status.replace(/[_-]/g, " ")),
              value: statusData.count
            };
          })
        };
      }
    }

    return null;
  }, [ totalTicketsByStatusResponse.data?.result, selectedView ]);

  if (selectedView !== "bar" && selectedView !== "line" && selectedView !== WidgetDisplayTypes.Number) {
    // Currently, graph bar and line are the only handled view options availble below
    return null;
  }

  return (
    <StyledWrapper ref={wrapperRef}>
      {totalTicketsByStatusResponse.pending && (
        <LoadingSpinner type="circular" />
      )}

      {graphDisplayData && graphMetrics && !totalTicketsByStatusResponse.error && (selectedView === "line" || selectedView === "bar") && (
        <>
          <WidgetInnerHeader title="Total Tickets" />

          <Graph
            containerWidth={width && width}
            hideLegend={xAxisBy === "status"}
            stacked={xAxisBy === "date"}
            data={graphDisplayData}
            type={selectedView}
            toolTipValueType="number"
            graphMetrics={graphMetrics}
            xAxis={{
              dataKey: xAxisBy,
              dateFormat: displayRange === "month" ? "month" : "day"
            }}
          />
        </>
      )}

      {numberDisplayData && fontSize && !totalTicketsByStatusResponse.error && (
        <NumberWidget
          widgetId={widgetId}
          displayData={numberDisplayData}
          valueType="number"
          units="tickets"
          fontSize={fontSize}
          hideToolTip
        />
      )}

      {totalTicketsByStatusResponse.error && (
        <DataLoadError errorMessage={totalTicketsByStatusResponse.error.message} />
      )}

    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    height: 100%;
    width: 100%;
    padding: ${theme.spacing.padding.sm} 0; 
  `}
`;
